import { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, Stack, Typography as T } from "@mui/material";
import useDataset from "datasets/hooks/useDataset";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import Switch from "~/components/Switch";
import useErrorToast from "~/contexts/error-toast";
import rumClient from "~/utils/monitoring";

const { VITE_CATALOG_API, VITE_ANALYSIS_AGENT_API } = import.meta.env;

interface ExternalServer {
  id: string;
  name: string;
  description?: string;
  is_external_mcp?: boolean;
}

const McpServerToggles = () => {
  const { data: dataset, mutate } = useDataset();
  const { showError } = useErrorToast();
  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;

  const [servers, setServers] = useState<ExternalServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [submitting, setSubmitting] = useState<string | null>(null);

  // Fetch MCP server list on mount
  useEffect(() => {
    let cancelled = false;

    fetch(`${VITE_ANALYSIS_AGENT_API}/mcp`)
      .then((resp) => {
        if (!resp.ok) throw new Error("Failed to fetch MCP servers");
        return resp.json();
      })
      .then((data: ExternalServer[]) => {
        if (cancelled) return;
        setServers(data.filter((s) => s.is_external_mcp));
      })
      .catch((e) => {
        console.error("Failed to load MCP servers:", e);
        if (!cancelled) setFetchError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const enabledSet = new Set(dataset?.externalMcpAccess ?? []);

  const handleToggle = useCallback(
    async (serverId: string, enabled: boolean) => {
      if (!dataset || submitting) return;

      setSubmitting(serverId);

      const currentEnabled = dataset.externalMcpAccess ?? [];
      const newEnabled = enabled
        ? [...currentEnabled, serverId]
        : currentEnabled.filter((id) => id !== serverId);

      try {
        const resp = await fetch(`${VITE_CATALOG_API}/datasets/${dataset.id}/external-mcp-access`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ serverIds: newEnabled }),
        });

        if (!resp.ok) {
          throw new Error("Failed to update enabled MCP servers");
        }

        mutate((current) => {
          if (!current || current.id !== dataset.id) return current;
          return { ...current, externalMcpAccess: newEnabled };
        });
      } catch (e) {
        console.error(e);
        rumClient?.recordError(e);
        showError("Unable to update connector settings");
      } finally {
        setSubmitting(null);
      }
    },
    [dataset, submitting, mutate, showError]
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (fetchError) {
    return (
      <T variant="body2" color="error">
        Failed to load connectors. Please try refreshing the page.
      </T>
    );
  }

  if (servers.length === 0) {
    return (
      <T variant="body2" color="text.secondary">
        No connectors available.
      </T>
    );
  }

  return (
    <Stack gap={1.5}>
      <T variant="body2" color="text.secondary">
        These connectors send queries to external, third-party services. Check with your internal
        team and organization before enabling.
      </T>
      {servers.map((server) => {
        const isEnabled = enabledSet.has(server.id);
        return (
          <Stack
            key={server.id}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            py={1}
          >
            <Box>
              <T fontSize={14} fontWeight={500}>
                {server.name}
              </T>
              {server.description && (
                <T variant="body2" color="text.secondary" fontSize={13}>
                  {server.description}
                </T>
              )}
            </Box>
            <Switch
              checked={isEnabled}
              disabled={!isOwner || submitting === server.id}
              onChange={(e) => handleToggle(server.id, e.target.checked)}
            />
          </Stack>
        );
      })}
    </Stack>
  );
};

export default McpServerToggles;
